<template lang="pug">
.vue-avatar--wrapper(:style="[style, customStyle]", aria-hidden="true")
  img.avatar-image(
    v-if="isImage",
    :src="imgSrc",
    :style="{ height: `${size}px`, width: `${size}px` }",
    aria-label="Profile Picture",
    @error="hideImage"
  )
  span.avatar-text(v-else) {{ userInitial }}
</template>

<script lang="ts" setup>
import { ref, computed, watch, onMounted } from "vue";
import type { AccountsTypes } from "@inctec/elearning-ui-client";
import * as settings from "../../settings.styl.json";
import { getUserPictureUrl } from "@/services/userPicture";
import { useStore } from "vuex";

interface Props {
  user?: AccountsTypes.User;
  username?: string;
  initials?: string;
  backgroundColor?: string;
  customStyle?: Record<string, any>;
  inline?: boolean;
  size?: number;
  rounded?: boolean;
  lighten?: number;
}

const props = withDefaults(defineProps<Props>(), {
  size: 50,
  rounded: true,
  lighten: 80,
  inline: false,
});

const emit = defineEmits<{
  (e: "avatar-initials", username: string, initials: string): void;
}>();

const isImage = ref(true);

onMounted(() => {});

const backgroundColors = settings.avatarColors;
const fontColors = settings.avatarFontColors;

// Compute username from user object or props
const computedUsername = computed(() => {
  if (props.username) return props.username;

  if (props.user) {
    const firstname = props.user.attributes?.firstname?.toString() ?? "";
    const lastname = props.user.attributes?.lastname?.toString() ?? "";
    const fullName = `${firstname} ${lastname}`.trim();

    return fullName.length > 0 ? fullName : props.user.username || "";
  }

  return "";
});

const store = useStore();

const profilePictureId = computed(() => store.getters["profilePictureId"]);

// Image source - use user ID if available
const imgSrc = computed(() => {
  if (props.user?.id) {
    // NOTE: the id string at the end is to reload the element and force reload the cache, it is not a backend feature
    return getUserPictureUrl(props.user.id, profilePictureId.value);
  }
  return "";
});

const background = computed(() => {
  if (!isImage.value && computedUsername.value) {
    return (
      props.backgroundColor ||
      randomBackgroundColor(computedUsername.value.length, backgroundColors)
    );
  }
  return "#FFF";
});

const fontColor = computed(() => {
  if (!isImage.value) {
    return fontColors
      ? randomBackgroundColor(computedUsername.value.length, fontColors)
      : lightenColor(background.value, props.lighten);
  }
  return "#000";
});

const style = computed(() => {
  const baseStyle: Record<string, any> = {
    display: props.inline ? "inline-flex" : "flex",
    width: `${props.size}px`,
    height: `${props.size}px`,
    borderRadius: props.rounded ? "50%" : 0,
    lineHeight: `${props.size + Math.floor(props.size / 20)}px`,
    fontWeight: "bold",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
  };

  const imgBackgroundAndFontStyle = {
    background: `transparent url('${imgSrc.value}') no-repeat scroll 0% 0% / ${props.size}px ${props.size}px content-box border-box`,
  };

  const initialBackgroundAndFontStyle = {
    backgroundColor: background.value,
    font: `${Math.floor(props.size / 2.5)}px/${props.size}px $accent-font`,
    color: fontColor.value,
  };

  const backgroundAndFontStyle = isImage.value
    ? imgBackgroundAndFontStyle
    : initialBackgroundAndFontStyle;

  Object.assign(baseStyle, backgroundAndFontStyle);

  return baseStyle;
});

const userInitial = computed(() => {
  if (!isImage.value && computedUsername.value) {
    const initials = props.initials || initial(computedUsername.value);
    return initials;
  }
  return "";
});

function initial(username: string): string {
  const parts = username.split(/[ -]/);
  let initials = "";

  for (let i = 0; i < parts.length; i++) {
    initials += parts[i].charAt(0);
  }

  if (initials.length > 3 && initials.search(/[A-Z]/) !== -1) {
    initials = initials.replace(/[a-z]+/g, "");
  }

  initials = initials.substr(0, 3).toUpperCase();

  return initials;
}

function hideImage() {
  isImage.value = false;
}

function randomBackgroundColor(seed: number, colors: string[]): string {
  return colors[seed % colors.length];
}

function lightenColor(hex: string, amt: number): string {
  let usePound = false;

  if (hex[0] === "#") {
    hex = hex.slice(1);
    usePound = true;
  }

  const num = parseInt(hex, 16);
  let r = (num >> 16) + amt;

  if (r > 255) r = 255;
  else if (r < 0) r = 0;

  let b = ((num >> 8) & 0x00ff) + amt;

  if (b > 255) b = 255;
  else if (b < 0) b = 0;

  let g = (num & 0x0000ff) + amt;

  if (g > 255) g = 255;
  else if (g < 0) g = 0;

  return (usePound ? "#" : "") + (g | (b << 8) | (r << 16)).toString(16);
}

// Watch for user changes to reset image state
watch(
  () => props.user?.id,
  () => {
    if (props.user?.id) {
      isImage.value = true;
    }
  },
);

onMounted(() => {
  if (!isImage.value) {
    emit("avatar-initials", computedUsername.value, userInitial.value);
  }
});
</script>

<style scoped>
.avatar-image {
  object-fit: cover;
  border-radius: 50%;
}

.avatar-text {
  font-size: 14px;
}
</style>
